import { For, Show } from 'solid-js';

import {
  computeAutoshipPrice,
  computeSavePercent,
  formatMoney,
  type RecommendedProduct,
} from '@/lib/cart-recommendations';

// `!` is required: keen-slider's stylesheet forces `.keen-slider__slide { min-width: ~100% }`
// for perView 'auto', which would otherwise stretch each card to the full track width.
const SLIDE_BASE = 'keen-slider__slide cart-rec-slide';

// Compact (sidecart drawer): wide + short, horizontal layout. Default (/cart page): narrow, widens on laptop+.
export const slideClass = (compact: boolean) =>
  compact
    ? `${SLIDE_BASE} min-w-[280px]! max-w-[280px]!`
    : `${SLIDE_BASE} min-w-[160px]! max-w-[160px]! laptop:min-w-[300px]! laptop:max-w-[300px]!`;

const StarRow = () => (
  <div class="flex text-accent-1 justify-center gap-1">
    <For each={[1, 2, 3, 4, 5]}>{() => <i class="fa-solid fa-star" />}</For>
  </div>
);

const Price = (props: { product: RecommendedProduct; autoshipLabel: string; compact?: boolean }) => {
  const savePct = () => computeSavePercent(props.product);
  const autoship = () => computeAutoshipPrice(props.product);
  return (
    <div>
      <div class="flex items-baseline gap-1.5 text-sm--body" classList={{ 'justify-center': !props.compact }}>
        <Show
          when={savePct() !== null}
          fallback={
            <Show
              when={props.product.price_varies}
              fallback={<span class="font-bold text-foreground">{formatMoney(props.product.price)}</span>}
            >
              <span class="font-bold text-foreground">
                {formatMoney(props.product.price_min)} - {formatMoney(props.product.price_max)}
              </span>
            </Show>
          }
        >
          <span class="font-bold text-foreground">{formatMoney(props.product.price)}</span>
          <s class="text-sm font-normal text-foreground/40">{formatMoney(props.product.compare_at_price!)}</s>
        </Show>
      </div>
      <Show when={autoship() !== null}>
        <p class="m-0 mt-0.5 text-xs">
          <span class="text-accent-2 font-bold">{formatMoney(autoship()!)}</span>{' '}
          <span class="text-foreground">{props.autoshipLabel}</span>
        </p>
      </Show>
    </div>
  );
};

const ActionButton = (props: { product: RecommendedProduct; cartUrl: string; compact?: boolean }) =>
  props.product.variants.length === 1 ? (
    <form action={props.cartUrl} method="post" class="m-0">
      <input type="hidden" name="id" value={String(props.product.variants[0]?.id)} />
      <button
        type="submit"
        aria-label={`Add ${props.product.title} to cart`}
        class="button button--primary w-full rounded-lg"
        classList={{ 'text-xs! py-2!': props.compact, 'text-sm': !props.compact }}
        innerHTML={`<add-to-cart-action form-state="initial" class="flex items-center justify-center gap-2"><span data-content>Add to Cart</span><template data-state="adding"><span class="flex items-center gap-2"><i class="fa-solid fa-spinner fa-spin"></i> Adding...</span></template><template data-state="added"><span class="flex items-center gap-2"><i class="fa-solid fa-check"></i> Added</span></template><template data-state="error"><span class="flex items-center gap-2"><i class="fa-solid fa-circle-exclamation"></i> Error</span></template></add-to-cart-action>`}
      />
    </form>
  ) : (
    <a
      href={props.product.url}
      aria-label={`View options for ${props.product.title}`}
      class="button button--secondary w-full text-center no-underline rounded-lg"
      classList={{ 'text-xs! py-2!': props.compact, 'text-sm': !props.compact }}
    >
      View More
    </a>
  );

type CardProps = { product: RecommendedProduct; cartUrl: string; autoshipLabel: string };

const SaveBadge = (props: { savePct: number | null }) => (
  <Show when={props.savePct !== null}>
    <p class="absolute top-0 right-0 bg-accent-2 text-white font-bold text-xs px-3 py-0.5 rounded-bl-xl m-0">
      Save {props.savePct}%
    </p>
  </Show>
);

// Default vertical card (used on the /cart page). Unchanged layout.
export const VerticalCard = (props: CardProps) => {
  const savePct = () => computeSavePercent(props.product);
  return (
    <div class={slideClass(false)}>
      <article class="p-3 bg-white border border-foreground/10 rounded-3xl shadow-sm overflow-hidden flex flex-col h-full card card__product">
        <a
          href={props.product.url}
          aria-label={props.product.title}
          class="block aspect-4/3 bg-background rounded-lg overflow-hidden no-underline mb-3 relative"
        >
          {props.product.featured_image && (
            <img
              src={props.product.featured_image}
              alt={props.product.title}
              class="w-full h-full object-cover"
              loading="lazy"
            />
          )}
          <SaveBadge savePct={savePct()} />
        </a>

        <div class="flex flex-col gap-2 pb-3 text-center">
          <StarRow />
          <a
            href={props.product.url}
            class="text-heading text-sm text-sm--body font-bold! no-underline hover:text-primary transition-colors line-clamp-2"
          >
            {props.product.title}
          </a>
          <Price product={props.product} autoshipLabel={props.autoshipLabel} />
        </div>

        <div class="mt-auto">
          <ActionButton product={props.product} cartUrl={props.cartUrl} />
        </div>
      </article>
    </div>
  );
};

// Compact horizontal card (used in the sidecart drawer): image left, content right — keeps height ~160px.
export const CompactCard = (props: CardProps) => {
  const savePct = () => computeSavePercent(props.product);
  return (
    <div class={slideClass(true)}>
      <article class="p-2 bg-white border border-foreground/10 rounded-3xl shadow-sm overflow-hidden flex flex-row gap-3 items-center h-full card card__product">
        <a
          href={props.product.url}
          aria-label={props.product.title}
          class="block w-24 aspect-square shrink-0 self-center bg-background rounded-lg overflow-hidden no-underline relative"
        >
          {props.product.featured_image && (
            <img
              src={props.product.featured_image}
              alt={props.product.title}
              class="w-full h-full object-cover"
              loading="lazy"
            />
          )}
          <SaveBadge savePct={savePct()} />
        </a>

        <div class="flex flex-col flex-1 min-w-0 gap-1 text-left">
          <a
            href={props.product.url}
            class="text-heading text-sm text-sm--body font-bold! no-underline hover:text-primary transition-colors line-clamp-1"
          >
            {props.product.title}
          </a>
          <Price product={props.product} autoshipLabel={props.autoshipLabel} compact />
          <ActionButton product={props.product} cartUrl={props.cartUrl} compact />
        </div>
      </article>
    </div>
  );
};

export const SkeletonCards = (props: { compact: boolean }) => (
  <div class="flex gap-3">
    <For each={[1, 2, 3]}>
      {() =>
        props.compact ? (
          <div class={`${slideClass(true)} animate-pulse`}>
            <div class="p-2 bg-white border border-foreground/10 rounded-3xl shadow-sm flex flex-row gap-3 items-center">
              <div class="rounded-lg bg-foreground/5 w-24 aspect-square shrink-0" />
              <div class="flex-1 flex flex-col gap-2">
                <div class="h-3 bg-foreground/5 rounded w-3/4" />
                <div class="h-3 bg-foreground/5 rounded w-1/3" />
                <div class="h-8 bg-foreground/5 rounded-lg w-full mt-1" />
              </div>
            </div>
          </div>
        ) : (
          <div class={`${slideClass(false)} animate-pulse`}>
            <div class="p-3 bg-white border border-foreground/10 rounded-3xl shadow-sm flex flex-col gap-3">
              <div class="rounded-lg bg-foreground/5 aspect-4/3" />
              <div class="h-3 bg-foreground/5 rounded w-1/2 mx-auto" />
              <div class="h-3 bg-foreground/5 rounded w-3/4 mx-auto" />
              <div class="h-3 bg-foreground/5 rounded w-1/3 mx-auto" />
              <div class="h-9 bg-foreground/5 rounded-lg w-full mt-1" />
            </div>
          </div>
        )
      }
    </For>
  </div>
);
